/**
 * The app: bar, rail, card list, and the Screener decision.
 *
 * The list renders from IndexedDB first and the relay fills in behind it, so
 * there is never a skeleton. Nothing shifts once drawn: every card is the same
 * height whether or not it carries tags, and the time column is fixed width
 * before any data arrives.
 */
import { el, clear, when, clock, initials, hueOf } from "./render";
import { icon, type IconName } from "./icons";
import { renderText, renderHtml, remoteImages, type ImageSource } from "./body";
import { autoLoad, bodyToFetch, fetchRemoteImage, imageParts } from "./images";
import { pollWhileVisible } from "./poll";
import { screenerPreview } from "./preview";
import { composer, type Composer } from "./compose";
import {
  accountAddress, dmReplyDraft, forwardDraft, newDraft, replyDraft, toRequest, type Draft,
} from "./draft";
import { sendOutbox } from "../nostr/outbox";
import type { Session } from "../session";
import type { Disposition, BlobRef } from "../nostr/seal";
import { unwrap, mailOf, isNotOurs, KIND_MAIL, type Mail } from "../nostr/seal";
import { dmOf, identify, defaultLookups, KIND_CHAT, type Identity } from "../nostr/dm";
import { ensureDmRelays } from "../nostr/dmrelays";
import { sendDm } from "../nostr/dmsend";
import { mailFilter, KIND_GIFT_WRAP } from "../nostr/relay";
import { fetchContacts, publishContacts, Allowlist } from "../nostr/contacts";
import { createDeleter } from "./deletes";
import { poof, closeGap } from "./poof";
import { fetchWorkflow, publishWorkflow, type WorkflowEntry } from "../nostr/workflow";
import { fetchImageSenders, publishImageSenders } from "../nostr/imagesenders";
import { conversationOf } from "../store/thread";
import { collectFiles, type FileRow } from "./files";
import { focusSession, type FocusSession } from "./focus";
import { fetchProfile, fetchAvatar, type Profile } from "../nostr/profile";
import { fetchBlob } from "../nostr/blossom";
import {
  byDisposition, countByDisposition, put, toStored, search, foldBody,
  markRead, lastSync, setLastSync, flagged, knownIds, screenings, recordScreening,
  refileSender, forgetSender, setFlag, cachedProfile, cacheProfile,
  cachedBlob, cacheBlob, cachedAvatar, cacheAvatar, displayName, senderNames,
  allMail, saveDraft, loadDraft, deleteDraft, composing, setComposing,
  addressOf, remove, deletedIds, rememberDeleted, purgeDeleted, hide, unhide, hidden,
  applyWorkflow, workflowFlags, isSurfaced, isNew, workflowPending, setWorkflowPending, expiredSpam,
  deletesMigrated, setDeletesMigrated,
  type StoredMail, type Screening,
} from "../store/db";

/** How long spam stays in the Spam box before it is deleted for good. */
const SPAM_DAYS = 7;

/** How long a decision waits for company before it is signed and published. */
const PUBLISH_SETTLE_MS = 800;
/** Wraps of one message, or of a burst, arrive together; wait for the rest. */
const LIVE_SETTLE_MS = 1500;

/** The four boxes are furniture: fixed order, fixed position, never a dropdown.
 * Each id is also its icon's name, so a box cannot ship without its mark. */
const BOXES: { id: IconName & Disposition; label: string }[] = [
  { id: "inbox", label: "Stack" },
  { id: "feed", label: "News" },
  { id: "paper_trail", label: "Paper Trail" },
  { id: "screener", label: "Maybes" },
];

/** Display names only. The ids stay as stored, so renaming a box never touches mail. */
const BOX_LABEL: Record<string, string> = {
  inbox: "Stack", feed: "News", paper_trail: "Paper Trail",
  screener: "Maybes", quarantine: "Spam",
};

interface Options {
  themePicker: () => HTMLSelectElement;
  onSignOut: () => Promise<void>;
  /**
   * Subscribe a redraw to a theme change, and return the unsubscribe.
   *
   * A theme change must repaint the shell. It must not re-enter `renderApp`,
   * which would build a second poller and sync at once.
   */
  onThemeChange?: (fn: () => void) => () => void;
}

/**
 * Everything the last render put outside `root`.
 *
 * `renderApp` runs again on every theme change and after sign-out, and a
 * listener or a timer registered on `document` survives the clearing of `root`.
 * Without this each theme change left another keydown handler and another sync
 * loop behind it, all of them live.
 */
let teardown: (() => void) | null = null;

/**
 * Release the last render's listeners and timers.
 *
 * `renderApp` calls it before drawing, and the boot path calls it too: signing
 * out ends at the sign-in screen, which never renders the app, so the sync loop
 * would otherwise keep ticking against a signer that has been closed.
 */
export function releaseApp(): void {
  teardown?.();
  teardown = null;
}

export async function renderApp(root: HTMLElement, session: Session, opts: Options): Promise<void> {
  releaseApp();
  const release: (() => void)[] = [];
  /** False once this account's app is gone. Late background work checks it. */
  let live = true;
  release.push(() => { live = false; });
  teardown = () => {
    for (const off of release.splice(0)) off();
  };
  clear(root);
  let box: Disposition = "inbox";
  let query = "";
  /** Whether the list slot holds the Files view rather than a box. */
  let filesView = false;
  /** Whether the list slot holds Settings rather than a box. */
  let settingsView = false;
  /** The message being read, or null for the list. One or the other, never both. */
  let opened: StoredMail | null = null;
  /** Object URLs for the open message's embedded images, released when it closes. */
  let imageUrls: string[] = [];
  /** The account avatar's object URL, replaced rather than accumulated. */
  let avatarUrl: string | null = null;
  /** The open composer, or null. It owns the list slot while it is there. */
  let writing: Composer | null = null;
  /**
   * The address this account sends from.
   *
   * It is read off the mail rather than typed: the registry lives on the
   * server, and postrd refuses a message whose key does not hold the address.
   */
  let sendAs = "";
  // Decisions outlive the tab, so the Screener never asks a second time.
  let decided: Record<string, Screening> = await screenings();
  /** Display names by address, from every message held. Refreshed each paint. */
  let names: Record<string, string> = await senderNames();
  /** Every message held, newest first. The reader threads it into conversations. */
  let held: StoredMail[] = [];
  /**
   * Delete: instant on screen, published behind it — `ui/deletes.ts`.
   *
   * A gift wrap cannot be withdrawn from a relay, so the published list is the
   * whole of delete: no sync opens an id that is on it, here or on a second
   * browser.
   */
  const deleter = createDeleter({
    relay: session.relay,
    signer: session.signer,
    pubkey: session.pubkey,
    store: {
      hide, unhide, hidden, remove, deletedIds, rememberDeleted, purgeDeleted,
      migrated: deletesMigrated, setMigrated: setDeletesMigrated,
    },
    starting: await deletedIds(),
    onChanged: () => {
      if (opened && deleter.has(opened.id)) leaveReader();
      void paint();
    },
    onRestored: (ids, err) => {
      status.textContent =
        `${ids.length === 1 ? "A message" : `${ids.length} messages`} could not be deleted and ${ids.length === 1 ? "is" : "are"} back: ${message(err)}`;
      void paint();
    },
  });

  /**
   * What this account decided about its mail, mirrored from the relay.
   *
   * The flags live on the record so lists and cards read one source, but the
   * answer is the map's, not the row's: a re-index that lost the flags gets
   * them back from the event on the next sync — `nostr/workflow.ts`.
   */
  let wfVersion = 0;
  /** When the published map was last read, so an unchanged one costs no decrypt. */
  let wfSeenAt = 0;
  /**
   * Bubble marks by message id, decided once per paint on one clock reading.
   *
   * A card built from a per-card `Date.now()` would surface or expire a bubble
   * depending on which millisecond its row was drawn in, so the whole list
   * shares one answer. Set during a repaint, read while cards are built.
   */
  let bubbledBy = new Map<string, HTMLElement>();

  /**
   * Which senders may load remote images in their mail, mirrored from the
   * relay. A grant made here publishes behind the click, and a grant made on
   * another browser arrives on the next sync — the same bargain every other
   * encrypted list here keeps.
   */
  let imageSenders: string[] = [];
  let imageSenderVersion = 0;
  let imageSenderSeenAt = 0;

  /** Messages in the open conversation the person has unfolded. */
  let unfolded = new Set<string>();
  /** The message a delete is waiting on an answer for. */
  let confirming: string | null = null;
  /** The keyboard session that walks the Reply Later stack. Null when off. */
  let focusing: FocusSession | null = null;

  /**
   * Drop focus mode without a repaint. A view change calls this before its
   * own paint, so a click on a box or Files is not answered by the stack.
   */
  function leaveFocusQuietly(): void {
    focusing?.release();
    focusing = null;
  }

  /**
   * Close focus mode and redraw. A key press here, an empty stack there, and
   * the list is back underneath either way.
   */
  function closeFocusMode(): void {
    leaveFocusQuietly();
    void paint();
  }

  /**
   * Start focus mode over the Reply Later stack.
   *
   * The question it answers: “which messages still want an answer?” It re-reads
   * the stack on every paint, so a message flagged from a card, the reader, or
   * another browser drops out the moment its flag clears.
   */
  function openFocusMode(): void {
    focusing?.release();
    focusing = focusSession<StoredMail>({
      stack: () => held.filter((m) => m.replyLater),
      render: (item) => detail(item, true),
      act: (item) => void openDraft(answerTo(item)),
      stop: () => closeFocusMode(),
      paused: () => !!writing || confirming !== null,
    });
    opened = null;
    filesView = false;
    void stopWriting().then(paint);
  }

  /**
   * What to call this sender.
   *
   * Their own header first, then the name they used on any other message, then
   * the address — which is the only case where the reader sees an address where
   * a name should be, because there is no name to be had.
   */
  function nameOf(item: StoredMail): string {
    return displayName(item.from) || names[item.senderAddress] || item.senderAddress || item.from;
  }

  const listEl = el("div", { class: "list-inner" });
  const countEls = new Map<Disposition, HTMLElement>();
  const status = el("div", { class: "status" }, "Reading local mail…");
  const heading = el("h1", {}, BOX_LABEL["inbox"]!);
  const newCount = el("span", { class: "new" });
  const screenerSlot = el("div", { class: "screener" });
  const stack = el("div", { class: "stack" });

  // ---- bar ----
  const searchInput = el("input", {
    class: "search", type: "search", placeholder: "Search everything…", "aria-label": "Search mail",
  });
  searchInput.addEventListener("input", () => {
    query = searchInput.value;
    settingsView = false;
    opened = null;
    // A search is a different list, so focus mode goes with the old one.
    leaveFocusQuietly();
    void stopWriting().then(paint);
  });
  const searchBox = el("div", { class: "searchbox" }, icon("search", 18), searchInput);

  const account = el(
    "button",
    { class: "account", type: "button", title: session.npub,
      "aria-haspopup": "menu", "aria-expanded": "false" },
  );
  paintAccount(null);

  /**
   * The signed-in identity, drawn from the account's own kind 0.
   *
   * Until one is found the npub stands in, because a placeholder name would be
   * a claim the client cannot support. The avatar is the one remote image in
   * the product: it is the account's own picture, chosen by the account, and it
   * degrades to the initial disc if it does not load.
   */
  function paintAccount(profile: Profile | null): void {
    clear(account);
    const name = profile?.name || `${session.npub.slice(0, 10)}…`;
    account.append(avatarFor(profile, name), el("span", { class: "who" }, name));
    account.title = profile?.nip05 ? `${profile.nip05} · ${session.npub}` : session.npub;
  }

  function avatarFor(profile: Profile | null, name: string): HTMLElement {
    // With no profile the disc carries two characters of the npub rather than
    // the "N" of "npub1…", which would be the same letter for every account.
    const letters = profile?.name ? initials(name) : session.npub.slice(5, 7).toUpperCase();
    const disc = el("span", { class: "disc", style: `--hue:${hueOf(session.pubkey)}` }, letters);
    // The picture arrives as bytes, so the disc is what is drawn now and the
    // photo replaces it once it is in hand. Nothing flashes and nothing is
    // left as a hole if the fetch fails.
    if (profile?.picture) void dressAvatar(profile.picture, disc);
    return disc;
  }

  async function dressAvatar(url: string, disc: HTMLElement): Promise<void> {
    try {
      let bytes = await cachedAvatar(url);
      if (!bytes) {
        const fetched = await fetchAvatar(url);
        if (!fetched) return;
        await cacheAvatar(url, fetched);
        bytes = fetched;
      }
      if (!disc.isConnected) return;
      if (avatarUrl) URL.revokeObjectURL(avatarUrl);
      avatarUrl = URL.createObjectURL(new Blob([bytes.slice().buffer]));
      disc.replaceWith(el("img", { class: "disc photo", src: avatarUrl, alt: "", decoding: "async" }));
    } catch {
      // Unreachable host, no CORS, a profile naming something that is not an
      // image: the initials stand, which is a complete answer on its own.
    }
  }

  async function loadAccount(): Promise<void> {
    const cached = await cachedProfile();
    if (cached) paintAccount(cached);
    try {
      const profile = await fetchProfile(session.pubkey);
      if (profile) {
        paintAccount(profile);
        await cacheProfile(profile);
      }
    } catch {
      // No profile relay reachable. The npub is a complete answer on its own.
    }
    try {
      await ensureDmRelays(session.signer, session.relayUrl);
    } catch (err) {
      // DMs from outside wait for the next load; mail is unaffected.
      console.warn("could not publish the DM relay list", err);
    }
  }

  /**
   * The account's menu: Settings and Sign out.
   *
   * Sign out is here rather than in the rail, because it is about the account
   * and not the mail, and the onboarding flow is only testable from a place a
   * signed-in person can always reach.
   */
  const settingsItem = el(
    "button",
    { class: "menu-item", type: "button", role: "menuitem" },
    icon("settings", 18),
    el("span", {}, "Settings"),
  );
  settingsItem.addEventListener("click", () => {
    closeAccountMenu();
    settingsView = true;
    filesView = false;
    opened = null;
    leaveFocusQuietly();
    heading.textContent = "Settings";
    for (const node of countEls.values()) {
      node.closest(".box")?.setAttribute("aria-current", String(false));
    }
    filesButton.setAttribute("aria-current", "false");
    spamButton.setAttribute("aria-current", "false");
    void stopWriting().then(paint);
  });

  const signOutItem = el(
    "button",
    { class: "menu-item", type: "button", role: "menuitem" },
    icon("exit", 18),
    el("span", {}, "Sign out"),
  );
  signOutItem.addEventListener("click", () => {
    closeAccountMenu();
    void signOutNow();
  });

  const accountMenu = el(
    "div",
    { class: "account-menu", role: "menu", "aria-label": "Account" },
    settingsItem,
    signOutItem,
  );
  const accountWrap = el("div", { class: "account-wrap" }, account, accountMenu);

  function closeAccountMenu(): void {
    accountMenu.classList.remove("is-open");
    account.setAttribute("aria-expanded", "false");
  }
  account.addEventListener("click", () => {
    if (accountMenu.classList.contains("is-open")) closeAccountMenu();
    else {
      accountMenu.classList.add("is-open");
      account.setAttribute("aria-expanded", "true");
    }
  });
  // A click anywhere else closes the menu, without touching anything under it.
  const onPointerDown = (e: PointerEvent): void => {
    if (!accountMenu.classList.contains("is-open")) return;
    if (e.target instanceof Node && accountWrap.contains(e.target)) return;
    closeAccountMenu();
  };
  document.addEventListener("pointerdown", onPointerDown);
  release.push(() => document.removeEventListener("pointerdown", onPointerDown));
  release.push(() => releaseRemoteImages());

  /**
   * Sign out wipes the store. Pending publishes finish first — a workflow map
   * read after the wipe would be empty and replace the real one — and nothing
   * still running may write into whoever signs in next.
   */
  async function signOutNow(): Promise<void> {
    status.textContent = "Signing out…";
    await settle().catch(() => {});
    live = false;
    await opts.onSignOut();
  }

  const newButton = el(
    "button",
    { class: "compose-new", type: "button", title: "Write a new message" },
    icon("compose", 18),
    el("span", { class: "label" }, "New Message"),
  );
  newButton.addEventListener("click", () => {
    leaveFocusQuietly();
    void openDraft(newDraft());
  });

  const bar = el(
    "header",
    { class: "topbar" },
    el("div", { class: "brand" },
      el("span", { class: "logo" },
        el("img", { src: "/brand/nicemail-symbol-small-accent.svg", alt: "", width: "26", height: "26" })),
      el("span", { class: "wordmark" }, "nicemail.ca")),
    searchBox,
    el("div", { class: "bar-tools" }, newButton, opts.themePicker(), accountWrap),
  );

  // ---- rail ----
  const rail = el("nav", { class: "rail", "aria-label": "Mailboxes" });
  rail.append(el("div", { class: "caption" }, "Navigation"));
  const boxesEl = el("div", { class: "boxes" });
  for (const def of BOXES) {
    const count = el("span", { class: "count" });
    countEls.set(def.id, count);
    const button = el(
      "button",
      { class: `box box-${def.id}`, type: "button", "aria-current": String(def.id === box) },
      icon(def.id),
      el("span", { class: "label" }, def.label),
      count,
    );
    button.addEventListener("click", () => {
      box = def.id;
      filesView = false;
      settingsView = false;
      leaveFocusQuietly();
      filesButton.setAttribute("aria-current", "false");
      spamButton.setAttribute("aria-current", "false");
      opened = null;
      heading.textContent = def.label;
      for (const [id, node] of countEls) {
        node.closest(".box")?.setAttribute("aria-current", String(id === box));
      }
      void stopWriting().then(paint);
    });
    boxesEl.append(button);
  }
  rail.append(boxesEl, el("div", { class: "divider" }));

  const filesButton = el(
    "button",
    { class: "box files", type: "button", "aria-current": "false" },
    icon("paperclip"),
    el("span", { class: "label" }, "Files"),
  );
  filesButton.addEventListener("click", () => {
    filesView = true;
    settingsView = false;
    leaveFocusQuietly();
    opened = null;
    heading.textContent = "Files";
    for (const node of countEls.values()) {
      node.closest(".box")?.setAttribute("aria-current", String(false));
    }
    filesButton.setAttribute("aria-current", "true");
    spamButton.setAttribute("aria-current", "false");
    void stopWriting().then(paint);
  });
  rail.append(filesButton);

  /**
   * Spam is filed, not refused, and kept out of the way: below the boxes, with
   * no count and no "new". It empties itself after a week.
   */
  const spamButton = el(
    "button",
    { class: "box spam", type: "button", "aria-current": "false" },
    icon("spam"),
    el("span", { class: "label" }, "Spam"),
  );
  spamButton.addEventListener("click", () => {
    box = "quarantine";
    filesView = false;
    settingsView = false;
    leaveFocusQuietly();
    opened = null;
    heading.textContent = "Spam";
    for (const node of countEls.values()) {
      node.closest(".box")?.setAttribute("aria-current", String(false));
    }
    filesButton.setAttribute("aria-current", "false");
    spamButton.setAttribute("aria-current", "true");
    void stopWriting().then(paint);
  });
  rail.append(spamButton);

  rail.append(el(
    "div",
    { class: "mark" },
    el("span", { class: "dots" }, el("i", {}), el("i", {}), el("i", {})),
    el("span", {}, "built by punk.science"),
  ));

  const listWrap = el("div", { class: "list" }, listEl);
  const main = el(
    "main",
    { class: "main" },
    el("div", { class: "head" }, heading, newCount),
    listWrap,
    status,
  );
  // The stored flag is still `replyLater`: renaming it would strand every
  // message already flagged in a browser's IndexedDB.
  const followUpHeading = el("h2", {}, "Follow Up");
  const focusOpen = el("button", { class: "focus-open", type: "button" },
    "Work Through These");
  focusOpen.addEventListener("click", () => openFocusMode());
  const aside = el("aside", { class: "aside" }, followUpHeading, focusOpen, stack, screenerSlot);

  root.append(el("div", { class: "app" }, bar, el("div", { class: "frame" }, rail, main, aside)));

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== "Escape") return;
    // The account menu closes before anything under it hears the Escape, so
    // leaving a menu never also leaves a message.
    if (accountMenu.classList.contains("is-open")) {
      closeAccountMenu();
      e.stopImmediatePropagation();
      return;
    }
    // Leaving the composer keeps what was written: Escape is how a person
    // stops, not how they throw a message away. When the shell consumes an
    // Escape, focus mode must not see it too: its own listener sits behind
    // this one, and `stopImmediatePropagation` is the wall between them.
    if (writing) {
      void closeComposer();
      e.stopImmediatePropagation();
      return;
    }
    if (confirming) {
      confirming = null;
      void paint();
      e.stopImmediatePropagation();
      return;
    }
    // The reader is the only surface left that Escape closes here. Focus mode
    // owns its own Escape, so this stands aside while a session is live.
    if (!focusing && opened) {
      leaveReader();
      void paint();
    }
  };
  document.addEventListener("keydown", onKeyDown);
  release.push(() => document.removeEventListener("keydown", onKeyDown));
  // A theme change and a sign-out both re-render the app. The composer's own
  // listener would outlive the node it belongs to, and so would focus mode's.
  release.push(() => {
    focusing?.release();
    focusing = null;
    writing?.release();
    writing = null;
  });

  /**
   * Paints are serialised.
   *
   * Every one of these clears a container and refills it after an await, so two
   * in flight at once interleave and the aside ends up with two of everything —
   * which is what happens the moment a click repaints while the sync's own
   * repaint is still reading IndexedDB.
   */
  let painting: Promise<void> = Promise.resolve();
  function paint(): Promise<void> {
    painting = painting.then(repaint, repaint);
    return painting;
  }

  /**
   * Redraw after a theme change, without starting a sync.
   *
   * The picker lives here, so it must repaint the client. A full `renderApp`
   * would build a second poller, and its first run syncs — signer round-trips,
   * and one Amber approval per message, for a cosmetic change. This repaints
   * from the store the reader already holds, so the open message, the box, and
   * the query all stay where they are.
   */
  function repaintForTheme(): void {
    void paint();
  }

  if (opts.onThemeChange) release.push(opts.onThemeChange(repaintForTheme));

  async function repaint(): Promise<void> {
    const items = filesView || settingsView
      ? []
      : query
        ? await search(query)
        : await byDisposition(box);
    held = await allMail();
    names = await senderNames();
    sendAs = accountAddress(held, session.domains);

    // One clock for the whole pass. Surfaced bubbles lead their box, newest
    // surface first, and only once the person opens the message — a bubble
    // that pinned itself forever teaches the reader to ignore the top of the
    // list, which is the one thing a bubble is for.
    const nowMs = Date.now();
    bubbledBy = new Map();
    for (const item of held) {
      if (!item.bubbleTo) continue;
      bubbledBy.set(item.id, el("div", {
        class: "bubble-note",
      }, isSurfaced(item, nowMs)
        ? `Bubbled up ${when(item.bubbleTo * 1000, nowMs)}`
        : `Returns ${when(item.bubbleTo * 1000, nowMs)}`));
    }
    items.sort((a, b) => {
      const sa = isSurfaced(a, nowMs) ? 1 : 0;
      const sb = isSurfaced(b, nowMs) ? 1 : 0;
      if (sa !== sb) return sb - sa;
      if (sa && sb) return (b.bubbleTo ?? 0) - (a.bubbleTo ?? 0);
      return b.dateMs - a.dateMs;
    });

    // The composer keeps the slot it is in. A background sync repaints the
    // counts around it, and rebuilding it would take the caret out of the
    // sentence somebody is typing.
    if (writing) {
      if (!listWrap.contains(writing.node)) {
        clear(listWrap);
        releaseImages();
        listWrap.append(writing.node);
      }
      await paintCounts(items);
      return;
    }

    clear(listWrap);
    releaseImages();
    if (opened) {
      // The open message is re-read from the store so a flag set inside the
      // reader is reflected without leaving it. From everything held, not from
      // the box's list: a conversation holds sent copies, which no box lists.
      opened = held.find((m) => m.id === opened?.id) ?? opened;
      listWrap.append(detail(opened));
    } else if (focusing) {
      listWrap.append(focusing.node);
      focusing.paint();
    } else if (settingsView) {
      clear(listEl);
      listEl.append(settingsPanel());
      listWrap.append(listEl);
    } else if (filesView) {
      clear(listEl);
      const rows = collectFiles(held, query, names);
      if (rows.length === 0) {
        listEl.append(el("div", { class: "empty" },
          query ? "Nothing matches." : "No files have arrived."));
      }
      for (const row of rows) listEl.append(fileCard(row));
      listWrap.append(listEl);
    } else {
      clear(listEl);
      if (items.length === 0) {
        listEl.append(el("div", { class: "empty" }, query ? "Nothing matches." : "Nothing here."));
      }
      // Search covers an offloaded body only once it is opened. Fetching every
      // body would cost one signer approval per blob, so the list says so instead.
      const unopened = query ? held.filter((m) => bodyToFetch(m)).length : 0;
      if (unopened > 0) {
        listEl.append(el("div", { class: "search-note" },
          `Search does not cover ${unopened} ${unopened === 1 ? "message" : "messages"} you have not opened. `
          + "Open a message once to make its text searchable."));
      }
      if (query) {
        for (const item of items) appendCard(listEl, item);
      } else {
        // New mail leads. Read mail keeps its order in time below it, under
        // a screen, so the new mail has no visual noise around it.
        const seen = items.filter((m) => !isNew(m, nowMs));
        for (const item of items) if (isNew(item, nowMs)) appendCard(listEl, item);
        if (seen.length > 0) listEl.append(readZone(seen));
      }
      listWrap.append(listEl);
    }

    await paintCounts(items);
  }

  /** One card, and the Set Aside choices under it when they are open. */
  function appendCard(into: HTMLElement, item: StoredMail): void {
    into.append(card(item));
    const panel = asidePanel(item);
    if (panel) into.append(panel);
  }

  /**
   * Read mail, under a screen.
   *
   * The screen is a blurred, semi-transparent cover over every read message. A
   * tap moves it down out of the way, and leaves its lip at the foot of the
   * list. A second tap moves it back up. The covered cards are inert, so the
   * keyboard and the screen reader skip what the eye cannot see.
   */
  let readUncovered = false;
  function readZone(seen: StoredMail[]): HTMLElement {
    const cards = el("div", { class: "read-cards" });
    for (const item of seen) appendCard(cards, item);
    const label = el("span", { class: "read-screen-label" });
    const screen = el("button", { class: "read-screen", type: "button" }, label);
    const zone = el("div", { class: "read-zone" }, cards, screen);
    const show = (): void => {
      zone.classList.toggle("is-uncovered", readUncovered);
      cards.toggleAttribute("inert", !readUncovered);
      screen.setAttribute("aria-expanded", String(readUncovered));
      label.textContent = readUncovered
        ? "Cover read mail"
        : `${seen.length} read — tap to show`;
    };
    screen.addEventListener("click", () => {
      readUncovered = !readUncovered;
      show();
    });
    show();
    return zone;
  }

  /** The rail's counts and the aside. Drawn whether or not the list is. */
  async function paintCounts(items: StoredMail[]): Promise<void> {
    const counts = await countByDisposition();
    for (const [id, node] of countEls) {
      const n = counts[id] ?? 0;
      node.textContent = n ? String(n) : "";
    }
    const nowMs = Date.now();
    const unread = items.filter((m) => isNew(m, nowMs)).length;
    newCount.textContent = box === "quarantine"
      ? (opened || writing ? "" : `Emptied after ${SPAM_DAYS} days`)
      : !opened && !writing && unread ? `${unread} New` : "";
    // One box colour per surface: the count wears the hue of the box it counts,
    // never the Inbox hue on a Feed screen.
    newCount.className = `new new-${box}`;

    clear(stack);
    const later = await flagged("replyLater");
    for (const item of later.slice(0, 6)) {
      stack.append(
        el("div", { class: "note-card" },
          el("div", { class: "who" }, nameOf(item)),
          el("div", { class: "subject" }, headline(item))),
      );
    }
    await paintScreener();

    // An empty list is nothing to report, so it says nothing: the heading goes
    // with it, and the column itself goes when the Screener is quiet too.
    followUpHeading.hidden = later.length === 0;
    focusOpen.hidden = later.length === 0 || !!focusing;
    stack.hidden = later.length === 0;
    aside.hidden = later.length === 0 && screenerSlot.childElementCount === 0;
  }

  /**
   * Open the composer on one draft.
   *
   * A draft already held for this message wins: pressing Reply a second time
   * returns to what was written the first time rather than starting again.
   */
  async function openDraft(fresh: Draft, notice?: string): Promise<void> {
    if (sending.has(fresh.id)) {
      status.textContent = "That message is still being sent.";
      return;
    }
    // Whatever is open is saved before it is replaced, not dropped.
    await stopWriting();
    const kept = await loadDraft(fresh.id);
    let draft = kept ?? fresh;
    if (draft.sendingSince) {
      // Not in flight on this page, yet marked: the page closed mid-send.
      const { sendingSince: _, ...settled } = draft;
      draft = settled;
      notice ??= "This was being sent when the page closed, and it may have gone. Send it again only if it did not arrive.";
    }
    await saveDraft(draft);
    await setComposing(draft.id);
    writing = composer({
      draft,
      account: draft.via === "nostr" ? session.npub : sendAs,
      onSave: saveDraft,
      onClose: () => void closeComposer(),
      onDiscard: async () => {
        await deleteDraft(draft.id);
        // Not `closeComposer`: that writes what is on screen, which would put
        // the draft straight back.
        writing?.release();
        writing = null;
        await setComposing(null);
        status.textContent = "The draft is gone.";
        await paint();
      },
      onSend: send,
      ...(notice ? { notice } : {}),
    });
    opened = null;
    await paint();
  }

  /**
   * Put the composer away, writing what is in it. The caller paints, so moving
   * to a box or a search is one repaint rather than two.
   */
  async function stopWriting(): Promise<void> {
    const open = writing;
    writing = null;
    if (!open) return;
    await open.flush();
    open.release();
    await setComposing(null);
  }

  /** Leave the composer. What was written stays in the draft. */
  async function closeComposer(): Promise<void> {
    await stopWriting();
    await paint();
  }

  /**
   * Send one draft.
   *
   * One press, one signature, one publish. postrd reads the relay every 15
   * seconds, so the message leaves within a minute and its sent copy comes back
   * on a later sync.
   *
   * An answer settles Follow Up: the message the draft answers leaves the
   * stack here, and the map tells every other browser. Republished once, with
   * the map, because a replaceable event answers "what is the state", never
   * "what changed".
   */
  /** Drafts on their way out. One cannot be reopened and sent a second time. */
  const sending = new Set<string>();

  /**
   * Send one draft: the composer closes on the click, and the message goes out
   * behind it. A failure, or a DM some recipients did not confirm, reopens the
   * draft with what was written and why — the draft is only deleted once the
   * relay has the message.
   */
  async function send(draft: Draft): Promise<void> {
    sending.add(draft.id);
    writing?.release();
    writing = null;
    opened = null;
    status.textContent = "Sending…";
    void paint();
    // On disk before anything goes out, so a reload mid-send cannot quietly
    // offer the same draft for a second send.
    const marked: Draft = { ...draft, sendingSince: Date.now() };
    await saveDraft(marked);
    await setComposing(null);
    void deliver(marked)
      .then((note) => note ?? null, (err: unknown) => `Not sent: ${message(err)}. What you wrote is kept.`)
      .then(async (note) => {
        sending.delete(draft.id);
        if (!note || !live) return;
        const { sendingSince: _, ...back } = (await loadDraft(draft.id)) ?? marked;
        await saveDraft(back);
        await openDraft(back, note);
      });
  }

  async function deliver(draft: Draft): Promise<void | string> {
    let sentTo: string;
    if (draft.via === "nostr") {
      const result = await sendDm(session.relay, {
        recipients: draft.recipients ?? [],
        text: draft.body.trim(),
        ...(draft.subject.trim() ? { subject: draft.subject.trim() } : {}),
        ...(draft.inReplyTo ? { inReplyTo: draft.inReplyTo } : {}),
      }, session.signer, draft.dmDelivery, async (delivery) => {
        draft.dmDelivery = delivery;
        draft.updated = Date.now();
        if (live) await saveDraft(draft);
      });
      sentTo = draft.to;
      if (!result.complete) {
        const count = result.failedRecipients.length;
        const recipientNote = count > 0
          ? `${count} ${count === 1 ? "recipient is" : "recipients are"} unconfirmed.`
          : "";
        const copyNote = !result.selfCopySent ? "The sent copy is unconfirmed." : "";
        const confirmed = result.delivery.deliveredRecipients.length + Number(result.selfCopySent);
        const progress = confirmed > 0 ? "Some copies were sent." : "No copies were confirmed.";
        return `${progress} ${[recipientNote, copyNote].filter(Boolean).join(" ")} Retry sends only missing copies.`;
      }
    } else {
      const request = toRequest(draft, sendAs);
      await sendOutbox(session.relay, request, session.signer, session.ingestPubkey);
      sentTo = request.to.join(", ");
    }
    // The relay has it. Nothing from here may reopen the draft for a retry,
    // which would send it twice; a draft left behind still carries its
    // sending mark, and reopens with that warning instead.
    try {
      await deleteDraft(draft.id);
      if (draft.source && (await flagged("replyLater")).some((m) => m.id === draft.source)) {
        await setFlag(draft.source, "replyLater", false);
        publishWorkflowSoon();
      }
      status.textContent = draft.via === "nostr"
        ? `Sent to ${sentTo} over Nostr.`
        : `Sent to ${sentTo}. It leaves within a minute.`;
    } catch (err) {
      status.textContent = `Sent to ${sentTo}, but tidying up here failed: ${message(err)}`;
    }
    await paint().catch(() => {});
  }

  /** A reply goes back the way the message came. */
  function answerTo(item: StoredMail, all = false): Draft {
    return item.origin === "nostr"
      ? dmReplyDraft(item, session.pubkey, all)
      : replyDraft(item, sendAs, all);
  }

  /** Reply, reply to all, forward. The three answers to a message. */
  function answers(item: StoredMail): HTMLElement {
    const reply = el("button", { class: "primary answer", type: "button" },
      icon("reply", 18), el("span", {}, "Reply"));
    reply.addEventListener("click", () => void openDraft(answerTo(item)));

    const all = el("button", { class: "secondary answer", type: "button" },
      icon("reply_all", 18), el("span", {}, "Reply to all"));
    all.addEventListener("click", () => void openDraft(answerTo(item, true)));

    const onward = el("button", { class: "secondary answer", type: "button" },
      icon("forward", 18), el("span", {}, "Forward"));
    onward.addEventListener("click", () => void openDraft(forwardDraft(item)));

    if (item.disposition === "quarantine" && !item.sent) {
      // Not spam is the Screener's Let In: the sender is trusted from now on,
      // and everything of theirs in the Spam box moves to the Inbox with it.
      const rescue = el("button", { class: "secondary answer", type: "button" },
        icon("inbox", 18), el("span", {}, "Not spam"));
      rescue.addEventListener("click", () => {
        leaveReader();
        void decide(item, true, "quarantine");
      });
      return el("div", { class: "answers" }, reply, all, onward, rescue);
    }
    return el("div", { class: "answers" }, reply, all, onward);
  }

  /**
   * One message, one card.
   *
   * The whole card is the target, so the open control is an overlay button with
   * the sender and subject as its label; the two flag controls sit above it.
   * Nesting them inside one button would be invalid and unreachable by keyboard.
   */
  function card(item: StoredMail): HTMLElement {
    const node = el("div", {
      class: `card${isNew(item, Date.now()) ? " is-unread" : ""}${item.sent ? " is-mine" : ""}`,
    });
    const name = cardName(item);

    const open = el("button", {
      class: "open", type: "button",
      "aria-label": `${name} — ${headline(item)}`,
    });
    open.addEventListener("click", () => {
      opened = item;
      leaveFocusQuietly();
      unfolded = new Set();
      confirming = null;
      // A surfaced bubble is seen once and gone: opening it clears the bubble.
      // The read mark lands first, so the flag write does not undo it.
      if (item.bubbleTo && isSurfaced(item, Date.now())) {
        void markRead(item.id).then(() => settleSurfaced(item));
      } else {
        void markRead(item.id).then(paint);
      }
    });

    // A message this account wrote is in a box only when it starts a
    // conversation, and then the box it is in says nothing about it.
    const tags = el(
      "div",
      { class: "tags" },
      item.sent
        ? el("span", { class: "tag tag-sent" }, "#sent")
        : el("span", { class: `tag tag-${item.disposition}` }, `#${tagFor(item)}`),
      originTag(item),
      bubNote(item),
    );

    const actions = el(
      "div",
      { class: "actions" },
      action("flag", "Follow Up", item.replyLater, () =>
        setDecision(item, "replyLater", !item.replyLater)),
      setAsideControl(item),
      // A delete from the list takes the card away at once, with no question.
      // The reader is where a message is read; the list is where it is filed,
      // and filing something out should not need a second click.
      action("trash", "Delete", false, () => deleteMessage(item, node)),
    );

    node.append(
      open,
      el("span", { class: "avatar", style: `--hue:${hueOf(counterparty(item))}` },
        initials(name)),
      el("div", { class: "card-body" },
        el("div", { class: "line" },
          el("span", { class: "who" }, name),
          el("span", { class: "when" }, when(item.dateMs))),
        el("div", { class: "subject" }, headline(item)),
        tags,
      ),
      actions,
    );
    return node;
  }

  /**
   * The bubble's line on the card, decided once per paint on one clock.
   */
  function bubNote(item: StoredMail): HTMLElement {
    return bubbledBy.get(item.id) ?? el("span", { class: "bubble-note is-empty" });
  }

  /**
   * The address a message is about.
   *
   * For mail that arrived it is the sender. For a message this account wrote it
   * is the first person it went to: a list of your own name tells you nothing,
   * and the disc and its colour should belong to the other person either way.
   */
  function counterparty(item: StoredMail): string {
    if (!item.sent) return item.senderAddress;
    if (item.origin === "nostr") return item.to?.[0] ?? "";
    return [...(item.to ?? []), ...(item.cc ?? [])].map(addressOf).find(Boolean) ?? "";
  }

  /** The name a card carries: the sender, or who your own message went to. */
  function cardName(item: StoredMail): string {
    if (!item.sent) return nameOf(item);
    const address = counterparty(item);
    return names[address] || address || "(nobody)";
  }

  /**
   * The reader, which reads a conversation rather than one message.
   *
   * There is no Sent box: an answer belongs under the message it answers, so
   * the whole thread is drawn, oldest first. The message that was clicked is
   * open and the rest are strips — a body can cost a blob fetch and, on a phone
   * signer, an approval, so a thread of ten must not cost ten of them.
   *
   * `bare` drops the Back control. Focus mode draws the same card but leaves
   * by `Escape`, and a Back button that returns to the same message reads as a
   * fault.
   */
  function detail(item: StoredMail, bare = false): HTMLElement {
    const thread = conversationOf(held, item.id);
    const node = el("article", { class: "detail" });
    if (!bare) {
      const back = el("button", { class: "back", type: "button" }, "← Back");
      back.addEventListener("click", () => {
        leaveReader();
        void paint();
      });
      node.append(el("div", { class: "detail-head" }, back,
        el("span", { class: "thread-count" },
          thread.length > 1 ? `${thread.length} messages` : "")));
    }
    node.append(el("h2", {}, headline(thread[0] ?? item)));

    for (const m of thread) {
      node.append(m.id === item.id || unfolded.has(m.id) ? unfoldedMessage(m) : strip(m));
    }
    node.append(answers(answerable(thread, item)));
    return node;
  }

  /**
   * Which message a Reply answers: the newest one somebody else wrote.
   *
   * Answering your own last word means writing to the person who has not
   * replied yet, and their address is on the message above it, not on yours.
   */
  function answerable(thread: StoredMail[], fallback: StoredMail): StoredMail {
    return [...thread].reverse().find((m) => !m.sent) ?? thread[thread.length - 1] ?? fallback;
  }

  /** Leave the reader. The thread folds up and any delete question goes with it. */
  function leaveReader(): void {
    opened = null;
    unfolded = new Set();
    confirming = null;
    bubblePicker = null;
    asidePicker = null;
  }

  /** A file row: one attachment, its sender, and where it arrived. */
  function fileCard(row: FileRow): HTMLElement {
    const { file, origin, sender } = row;
    const name = file.filename || "(unnamed)";
    const open = el("button", {
      class: "open", type: "button",
      "aria-label": `${name} from ${displayOf(sender)} — open the message`,
    });
    open.addEventListener("click", () => {
      opened = origin;
      filesView = false;
      settingsView = false;
      leaveFocusQuietly();
      filesButton.setAttribute("aria-current", "false");
      box = origin.sent ? "inbox" : origin.disposition;
      const boxBtn = rail.querySelector(`.box-${box}`);
      boxBtn?.setAttribute("aria-current", "true");
      unfolded = new Set([origin.id]);
      void markRead(origin.id).then(paint);
    });

    const save = el("button", { class: "act", type: "button", title: `Download ${name}`,
      "aria-label": `Download ${name}` }, icon("download", 18));
    save.addEventListener("click", () => {
      save.disabled = true;
      void openBlob(file)
        .then((bytes) => download(name, file.content_type, bytes))
        .catch((err: unknown) => {
          status.textContent = `${name} did not fetch: ${message(err)}`;
        })
        .finally(() => {
          save.disabled = false;
        });
    });

    return el("div", { class: "file-card" },
      open,
      el("span", { class: "avatar", style: `--hue:${hueOf(sender)}` }, initials(displayOf(sender))),
      el("div", { class: "card-body" },
        el("div", { class: "line" },
          el("span", { class: "who" }, displayOf(sender)),
          el("span", { class: "when" }, when(origin.dateMs))),
        el("div", { class: "filename" }, name),
        el("div", { class: "tags" },
          el("span", { class: "file-size" }, kb(file.size)),
          el("span", { class: "when" }, headline(origin)))),
      el("div", { class: "actions" }, save));
  }

  /** Name for a sender address in the Files list. */
  function displayOf(address: string): string {
    return names[address] || address;
  }

  /**
   * Settings, in the list's slot: the senders permitted to load remote images.
   *
   * The list is state on the relay, so it holds what was granted here or on
   * another browser, and a removal publishes to both.
   */
  function settingsPanel(): HTMLElement {
    const rows = imageSenders.map((address) => {
      const name = names[address] || address;
      const remove = el("button", { class: "settings-remove", type: "button" }, "Remove");
      remove.addEventListener("click", () => void revokeImageSender(address));
      return el("div", { class: "settings-row" },
        el("span", { class: "avatar", style: `--hue:${hueOf(address)}` }, initials(name)),
        el("div", { class: "card-body" },
          el("div", { class: "who" }, name),
          el("div", { class: "address" }, address)),
        remove);
    });
    return el("div", { class: "settings" },
      el("h2", {}, "Remote images"),
      el("p", { class: "settings-note" },
        "A remote image tells its sender that this mail was opened. Every sender is blocked until one is permitted here. A permitted sender's images load on open, on every device."),
      imageSenders.length === 0
        ? el("div", { class: "empty" }, "No sender may load remote images.")
        : el("div", { class: "settings-rows" }, ...rows));
  }

  let bubblePicker: string | null = null;

  /**
   * Bubble Up: resurface a message on a future date. No server scheduler; the
   * date passes while the tab is shut and the next load pins the message.
   *
   * Opening a surfaced message clears its bubble, so a bubble is seen once
   * and done — pinning forever teaches the reader to ignore the top.
   */
  function bubUp(item: StoredMail): HTMLElement {
    const hasBubble = !!item.bubbleTo;
    const button = el("button", {
      class: `act${hasBubble ? " is-on" : ""}`, type: "button",
      "aria-pressed": String(hasBubble), "aria-label": "Bubble Up",
      title: "Bubble Up",
    }, icon("bubble", 18));
    button.addEventListener("click", () => {
      bubblePicker = bubblePicker === item.id ? null : item.id;
      asidePicker = null;
      void paint();
    });
    return button;
  }

  function bubblePanel(item: StoredMail): HTMLElement | null {
    if (bubblePicker !== item.id) return null;

    const tomorrow = Math.floor((Date.now() + 86_400_000) / 1000);
    const nextWeek = Math.floor((Date.now() + 7 * 86_400_000) / 1000);

    const pick = (label: string, epoch: number): HTMLButtonElement => {
      const btn = el("button", { class: "bubble-preset", type: "button" }, label);
      btn.addEventListener("click", () => {
        bubblePicker = null;
        void setDecision(item, "bubbleTo", epoch);
      });
      return btn;
    };

    const clear = el("button", { class: "bubble-clear", type: "button" }, "Cancel bubble");
    clear.addEventListener("click", () => {
      bubblePicker = null;
      void setDecision(item, "bubbleTo", undefined);
    });

    // A plain date is the third way to say when. Midnight local on that day.
    const date = el("input", { class: "bubble-date", type: "date", "aria-label": "Bubble up on a date" });
    date.addEventListener("change", () => {
      const ms = Date.parse(`${date.value}T00:00:00`);
      if (Number.isNaN(ms)) return;
      bubblePicker = null;
      void setDecision(item, "bubbleTo", Math.floor(ms / 1000));
    });

    const panel = el("div", { class: "bubble-panel" },
      pick("Tomorrow", tomorrow),
      pick("Next week", nextWeek),
      date,
      item.bubbleTo ? clear : "",
    );
    return panel;
  }

  /**
   * Set Aside: hold a message out of its box until a chosen time.
   *
   * The message leaves the list at once and returns at the top, as new, when
   * the time passes. The time is the bubble date, so it reaches every device
   * with the rest of the workflow map, and no server wakes for it. A second
   * press on a set-aside message brings it back now.
   */
  let asidePicker: string | null = null;
  function setAsideControl(item: StoredMail): HTMLButtonElement {
    return action("clock", "Set Aside", item.setAside, async () => {
      if (item.setAside) {
        asidePicker = null;
        await setAsideUntil(item, undefined);
        return;
      }
      asidePicker = asidePicker === item.id ? null : item.id;
      bubblePicker = null;
      await paint();
    });
  }

  function asidePanel(item: StoredMail): HTMLElement | null {
    if (asidePicker !== item.id) return null;
    const pick = (label: string, days: number): HTMLButtonElement => {
      const btn = el("button", { class: "bubble-preset", type: "button" }, label);
      btn.addEventListener("click", () => {
        asidePicker = null;
        void setAsideUntil(item, Math.floor((Date.now() + days * 86_400_000) / 1000));
      });
      return btn;
    };
    const keep = el("button", { class: "bubble-clear", type: "button" }, "Keep it here");
    keep.addEventListener("click", () => {
      asidePicker = null;
      void paint();
    });
    return el("div", { class: "bubble-panel aside-panel", role: "group", "aria-label": "Set aside until" },
      pick("Tomorrow", 1),
      pick("In a Few Days", 3),
      pick("Next Week", 7),
      keep);
  }

  /**
   * Write a Set Aside decision. `until` undefined brings the message back now.
   *
   * The message is marked unread, so it returns as new mail. Setting aside the
   * open message leaves the reader, because the message has left the list.
   */
  async function setAsideUntil(item: StoredMail, until: number | undefined): Promise<void> {
    await setFlag(item.id, "setAside", until !== undefined);
    await setFlag(item.id, "bubbleTo", until);
    if (until !== undefined) {
      await markRead(item.id, false);
      if (opened?.id === item.id) leaveReader();
    }
    await paint();
    publishWorkflowSoon();
  }

  /** Opening a surfaced message settles it: it is seen, so it stops returning. */
  async function settleSurfaced(item: StoredMail): Promise<void> {
    if (item.setAside) await setFlag(item.id, "setAside", false);
    await setDecision(item, "bubbleTo", undefined);
  }

  /**
   * One message in the conversation, open.
   *
   * The body is never live HTML from the sender: it is parsed inert and rebuilt
   * from an allowlist, which is the one place a tracking pixel or a remote font
   * would otherwise get into a client that exists to not have them.
   */
  function unfoldedMessage(item: StoredMail): HTMLElement {
    const node = el("section", { class: `msg${item.sent ? " is-mine" : ""}` });
    node.append(
      el("div", { class: "msg-head" },
        el("span", { class: "avatar", style: `--hue:${hueOf(counterparty(item))}` },
          initials(cardName(item))),
        who(item),
        el("div", { class: "actions" },
          action("flag", "Follow Up", item.replyLater, () =>
            setDecision(item, "replyLater", !item.replyLater)),
          setAsideControl(item),
          bubUp(item),
          action("trash", "Delete", confirming === item.id, async () => {
            confirming = confirming === item.id ? null : item.id;
            await paint();
          }))),
      el("div", { class: "tags" },
        item.sent
          ? el("span", { class: "tag tag-sent" }, "#sent")
          : el("span", { class: `tag tag-${item.disposition}` }, `#${tagFor(item)}`),
        originTag(item),
        el("span", { class: "when" }, when(item.dateMs)),
        bubNote(item)),
    );
    if (confirming === item.id) node.append(confirmDelete(item));
    const panel = bubblePanel(item) ?? asidePanel(item);
    if (panel) node.append(panel);

    const bodySlot = el("div", { class: "body" });
    node.append(bodySlot);
    void fillBody(item, bodySlot, node);

    // Embedded images belong in the body, not in the list of files to save.
    const files = (item.attachments ?? []).filter((a) => !a.inline);
    if (files.length > 0) {
      node.append(el("div", { class: "attachments" }, ...files.map(attachment)));
    }
    return node;
  }

  /**
   * One message in the conversation, folded.
   *
   * It carries who and when and nothing of the body. A preview snippet is the
   * one thing this client is built not to show, and unfolding may cost a fetch.
   */
  function strip(item: StoredMail): HTMLElement {
    const node = el("button", {
      class: `strip${isNew(item, Date.now()) ? " is-unread" : ""}${item.sent ? " is-mine" : ""}`,
      type: "button",
      "aria-label": `Open the message from ${cardName(item)}`,
    },
      el("span", { class: "avatar", style: `--hue:${hueOf(counterparty(item))}` },
        initials(cardName(item))),
      el("span", { class: "who" }, item.sent ? "You" : cardName(item)),
      el("span", { class: "when" }, when(item.dateMs)));
    node.addEventListener("click", () => {
      unfolded.add(item.id);
      if (item.bubbleTo && isSurfaced(item, Date.now())) {
        void markRead(item.id).then(() => settleSurfaced(item));
      } else {
        void markRead(item.id).then(paint);
      }
    });
    return node;
  }

  /**
   * Delete asks once, and both answers are final.
   *
   * There is no undo. The plaintext is in this browser and nowhere else, and
   * the relay keeps only a wrap nobody can open again.
   */
  function confirmDelete(item: StoredMail): HTMLElement {
    const yes = el("button", { class: "danger", type: "button" }, "Delete for good");
    const no = el("button", { class: "keep", type: "button" }, "Keep");
    yes.addEventListener("click", () => void deleteMessage(item, yes.closest(".msg") ?? yes.closest(".detail")));
    no.addEventListener("click", () => {
      confirming = null;
      void paint();
    });
    return el("div", { class: "confirm" },
      el("span", { class: "note" }, "Delete this message? This cannot be undone."), yes, no);
  }

  /**
   * Delete one message, now.
   *
   * The card goes in the frame of the click and the smoke plays where it was.
   * Everything slow — the signature, the relay — happens behind the screen,
   * and a delete that cannot be published comes back on its own.
   */
  async function deleteMessage(item: StoredMail, from?: Element | null): Promise<void> {
    confirming = null;
    const rest = conversationOf(held, item.id).filter((m) => m.id !== item.id);
    unfolded.delete(item.id);
    const slot = from?.classList.contains("card") ? from : null;
    const at = slot ? [...(slot.parentElement?.children ?? [])].indexOf(slot) : -1;
    const height = slot?.getBoundingClientRect().height ?? 0;
    if (from) poof(from);
    await deleter.delete(item.id);
    if (opened?.id === item.id) opened = rest[rest.length - 1] ?? null;
    await paint();
    if (at >= 0) closeGap(listEl, at, height);
  }

  /**
   * Mirror the workflow map from the relay into the held mail.
   *
   * The skip-read is the same bargain `readDeleted` strikes: the poller runs
   * every minute, and decrypting a map nobody rewrote costs a phone approval
   * for nothing. A browser that was shut while another decided catches up on
   * the first sync, and applying is silent — a decision heard is not a
   * decision made here, so it does not re-publish.
   */
  /**
   * The map as last read. Kept because it is read before the mail: on a new
   * device it applies to nothing, the next read is skipped as unchanged, and
   * the mail that arrives after would never get its flags.
   */
  let wfFlags: Readonly<Record<string, WorkflowEntry>> | null = null;

  async function readWorkflow(): Promise<void> {
    const record = await fetchWorkflow(
      session.relay, session.pubkey, session.signer, wfSeenAt,
    );
    if (!record) return;
    wfSeenAt = record.createdAt;
    wfVersion = Math.max(wfVersion, record.map.version);
    wfFlags = record.map.flags;
    const moved = await applyWorkflow(record.map.flags);
    if (moved && opened) opened = (await allMail()).find((m) => m.id === opened?.id) ?? opened;
  }

  /**
   * Set one message's flag here, then tell every other browser this key opens.
   *
   * The row writes first and the map publishes second: a decision that never
   * reaches the relay is still a decision made, seen on this device and
   * reported in the status line — the same honesty delete keeps about a list
   * that did not land. The whole map goes each time, because a replaceable
   * event only answers "what is the state", never "what changed". The repaint
   * re-reads both, so nothing here updates a card by hand.
   */
  async function setDecision(
    item: StoredMail,
    flag: "replyLater" | "setAside" | "bubbleTo",
    value: boolean | number | undefined,
  ): Promise<void> {
    await setFlag(item.id, flag, value);
    await paint();
    publishWorkflowSoon();
  }

  /**
   * Publish the map a moment after the last decision, so a run of clicks is
   * one signature, and never two publishes at once — both would claim the
   * same version.
   */
  let wfTimer: ReturnType<typeof setTimeout> | null = null;
  let wfChain: Promise<void> = Promise.resolve();
  /** Bumped by every decision, so a publish knows whether it carried the last one. */
  let wfDecisions = 0;
  function publishWorkflowSoon(): void {
    wfDecisions += 1;
    void setWorkflowPending(true);
    if (wfTimer) clearTimeout(wfTimer);
    wfTimer = setTimeout(() => {
      wfTimer = null;
      wfChain = wfChain.then(publishWorkflowMap);
    }, PUBLISH_SETTLE_MS);
  }
  /** Finish every queued publish now. Sign-out waits on this before the wipe. */
  async function settle(): Promise<void> {
    if (wfTimer) {
      clearTimeout(wfTimer);
      wfTimer = null;
      wfChain = wfChain.then(publishWorkflowMap);
    }
    await wfChain;
    if (senderTimer) {
      clearTimeout(senderTimer);
      senderTimer = null;
      senderChain = senderChain.then(publishImageSenderList);
    }
    await senderChain;
    await deleter.flush();
    await contactsChain;
  }

  // Leaving inside the delay publishes now rather than dropping the decision;
  // if that fails too, the pending mark on disk is the next sync's to finish.
  release.push(() => {
    if (!wfTimer) return;
    clearTimeout(wfTimer);
    wfTimer = null;
    wfChain = wfChain.then(publishWorkflowMap);
  });
  // The same for a permission granted inside the delay before a re-render.
  release.push(() => {
    if (!senderTimer) return;
    clearTimeout(senderTimer);
    senderTimer = null;
    senderChain = senderChain.then(publishImageSenderList);
  });

  async function publishWorkflowMap(): Promise<void> {
    // Counted before the flags are read: a decision made after this point is
    // not in this map, so this publish must leave it pending.
    const carried = wfDecisions;
    try {
      const flags = await workflowFlags();
      const event = await publishWorkflow(
        session.relay, session.signer, flags, wfVersion + 1, wfSeenAt,
      );
      wfVersion += 1;
      wfSeenAt = event.created_at;
      // What was published is now the map, or new mail would get older flags.
      wfFlags = flags;
      if (carried === wfDecisions) await setWorkflowPending(false);
    } catch (err) {
      status.textContent = `Saved here. The decision did not reach the relay: ${message(err)}`;
    }
  }

  /**
   * Publish the sender list a moment after the last grant, so a run of clicks
   * is one signature, and never two publishes at once — both would claim the
   * same version.
   */
  let senderTimer: ReturnType<typeof setTimeout> | null = null;
  let senderChain: Promise<void> = Promise.resolve();
  function publishImageSendersSoon(): void {
    if (senderTimer) clearTimeout(senderTimer);
    senderTimer = setTimeout(() => {
      senderTimer = null;
      senderChain = senderChain.then(publishImageSenderList);
    }, PUBLISH_SETTLE_MS);
  }

  async function publishImageSenderList(): Promise<void> {
    try {
      const event = await publishImageSenders(
        session.relay, session.signer, imageSenders, imageSenderVersion + 1, imageSenderSeenAt,
      );
      imageSenderVersion += 1;
      imageSenderSeenAt = event.created_at;
    } catch (err) {
      status.textContent = `The permission did not reach the relay: ${message(err)}`;
    }
  }

  /** Grant a sender remote images, from now on and on every device. */
  function permitImageSender(address: string): void {
    const added = address.trim().toLowerCase();
    if (!added || imageSenders.includes(added)) return;
    imageSenders = [...imageSenders, added].sort();
    publishImageSendersSoon();
  }

  /** Revoke a sender's images, here and on every device. */
  async function revokeImageSender(address: string): Promise<void> {
    imageSenders = imageSenders.filter((a) => a !== address);
    status.textContent = `${names[address] || address} is blocked again.`;
    await paint();
    publishImageSendersSoon();
  }

  /**
   * Mirror the list off the relay, and only when it has moved. A grant made on
   * another browser repaints, because the open message may owe its images to it.
   */
  async function readImageSenders(): Promise<void> {
    const record = await fetchImageSenders(
      session.relay, session.pubkey, session.signer, imageSenderSeenAt,
    );
    if (!record) return;
    imageSenderSeenAt = record.createdAt;
    imageSenderVersion = Math.max(imageSenderVersion, record.list.version);
    const next = record.list.senders;
    const changed =
      next.length !== imageSenders.length || next.some((a, i) => a !== imageSenders[i]);
    imageSenders = next;
    if (changed) await paint();
  }

  /**
   * Name over address — and "You" over who it went to for your own message.
   *
   * The address alone is shown where there is genuinely no name, which is the
   * only place the reader prints one where a name should be.
   */
  function who(item: StoredMail): HTMLElement {
    if (item.sent) {
      const to = [...(item.to ?? []), ...(item.cc ?? [])].map(addressOf).filter(Boolean);
      const block = el("div", {}, el("div", { class: "who" }, "You"));
      if (to.length > 0) block.append(el("div", { class: "address" }, `to ${to.join(", ")}`));
      return block;
    }
    const name = nameOf(item);
    const address = item.senderAddress || item.from;
    const block = el("div", {}, el("div", { class: "who" }, name));
    if (name !== address) block.append(el("div", { class: "address" }, address));
    return block;
  }

  /**
   * Put the body on screen: markup when there is any, plain text otherwise.
   *
   * HTML is preferred now that it renders safely, because it is what the sender
   * laid out — the plain part of a two-part message is usually a flattened
   * copy with the links spelled out in brackets.
   */
  async function fillBody(
    item: StoredMail,
    slot: HTMLElement,
    card: HTMLElement,
    autoImages = true,
  ): Promise<void> {
    let html = item.html?.trim() ?? "";
    let text = item.text?.trim() ?? "";

    const offloaded = bodyToFetch(item);
    if (offloaded) {
      // A third of real mail is too big to seal, so its body lives in the blob
      // store. Fetching starts here rather than on a button: the message is
      // open, which is the whole statement of intent.
      if (!text) {
        slot.className = "body missing";
        slot.textContent = "Fetching the body…";
      }
      try {
        const decoded = new TextDecoder().decode(await openBlob(offloaded));
        if (offloaded === item.html_ref) html = decoded.trim();
        else text = decoded.trim();
        // Kept on the message for this session, so re-rendering after images
        // load does not flash "Fetching the body…" over a body already held.
        if (html) item.html = html;
        if (text) item.text = text;
        await foldBody(item.id, { html, text }).catch((err) => console.warn("could not index the body", err));
      } catch (err) {
        // A plain copy is a worse body, not no body.
        if (!text) {
          slot.className = "body missing";
          slot.textContent = `Could not fetch the body: ${message(err)}`;
          return;
        }
      }
    }

    // Rendering happens after every await that can fail, so a body is never
    // cleared and then left empty by an exception nobody sees.
    let rendered: DocumentFragment | null = null;
    let pending: BlobRef[] = [];
    let figures: HTMLElement[] = [];
    try {
      // One run of this body owns one list of URLs, and the run that replaces
      // it releases them before it draws.
      releaseBodyImages();
      const parts = await messageImages(item.attachments ?? [], bodyUrls);
      pending = parts.pending;
      figures = parts.figures;
      // A remote image the page fetched renders from the object URL it was
      // handed; one that was not stays blocked, the CSP's own rule.
      const source: ImageSource = {
        ...parts.images,
        remote: (address) => remoteHeld.get(address) ?? null,
      };
      if (html) rendered = renderHtml(html, source);
    } catch (err) {
      slot.className = "body missing";
      slot.textContent = `Could not render this message: ${message(err)}`;
      return;
    }

    clear(slot);
    // The button belongs to the render that produced it, so a redraw replaces
    // it rather than standing a second one beside the first.
    card.querySelector(".load-images")?.remove();
    card.querySelector(".remote-controls")?.remove();
    if (rendered) {
      slot.className = "body rich";
      slot.append(rendered);
    } else if (text) {
      slot.className = "body plain";
      slot.append(renderText(text));
    } else if (figures.length > 0) {
      slot.className = "body rich";
    } else {
      slot.className = "body missing";
      slot.textContent = "This message has no text body.";
    }
    slot.append(...figures);

    paintRemoteImages(item, slot, card, html);

    if (pending.length > 0) {
      const { load, defer } = autoImages
        ? autoLoad(pending, session.signer.remote)
        : { load: [], defer: pending };
      if (defer.length > 0) card.append(loadImages(defer, item, slot, card));
      if (load.length > 0) void carriedImages(load, item, slot, card);
    }
  }

  /**
   * Fetch the parts the message itself carries, then draw the body again.
   *
   * Exactly one pass: the redraw is told not to auto-fetch, so whatever failed
   * here is offered behind the button instead of being asked for forever. A
   * failure is swallowed on purpose — the placeholder already says the image is
   * not loaded, and a body half-drawn over an exception says nothing at all.
   */
  async function carriedImages(
    refs: BlobRef[],
    item: StoredMail,
    slot: HTMLElement,
    card: HTMLElement,
  ): Promise<void> {
    // One at a time for a remote signer: each fetch is an approval on the
    // user's phone, and parallel ones arrive there as a stack of prompts.
    if (session.signer.remote) {
      for (const ref of refs) await openBlob(ref).catch(() => null);
    } else {
      await Promise.all(refs.map((ref) => openBlob(ref).catch(() => null)));
    }
    if (slot.isConnected) await fillBody(item, slot, card, false);
  }

  /**
   * Everything a message carries that is meant to be looked at: `cid:` parts the
   * body places itself, and attached images, which are drawn beneath it.
   *
   * An attached image keeps its pill — the file is still the point — but a
   * photo nobody can see until they download it is a mail client failing at the
   * one thing the message was sent for.
   *
   * Only cached blobs are used here, so this never costs a signature; the
   * caller decides which of the rest are worth fetching on open and which wait
   * behind the button.
   */
  async function messageImages(
    refs: readonly BlobRef[],
    into: string[] = bodyUrls,
  ): Promise<{ images: ImageSource; pending: BlobRef[]; figures: HTMLElement[] }> {
    const { embedded, attached } = imageParts(refs);
    const urls = new Map<string, string>();
    const pending: BlobRef[] = [];
    for (const ref of embedded) {
      const bytes = await cachedBlob(ref.sha256);
      if (bytes) urls.set(ref.content_id!, objectUrl(bytes, ref.content_type, into));
      else pending.push(ref);
    }
    const figures: HTMLElement[] = [];
    for (const ref of attached) {
      const bytes = await cachedBlob(ref.sha256);
      if (bytes) figures.push(figure(ref, objectUrl(bytes, ref.content_type, into)));
      else pending.push(ref);
    }
    const carried = new Set(embedded.map((ref) => ref.content_id!));
    return {
      images: { resolve: (cid) => urls.get(cid) ?? null, has: (cid) => carried.has(cid) },
      pending,
      figures,
    };
  }

  function figure(ref: BlobRef, url: string): HTMLElement {
    return el("figure", { class: "figure" }, el("img", {
      src: url, alt: ref.filename || "", loading: "lazy", decoding: "async",
    }));
  }

  function loadImages(
    pending: BlobRef[],
    item: StoredMail,
    slot: HTMLElement,
    card: HTMLElement,
  ): HTMLElement {
    const label = pending.length === 1 ? "Load 1 image" : `Load ${pending.length} images`;
    const button = el("button", { class: "secondary load-images", type: "button" }, label);
    button.addEventListener("click", () => {
      button.disabled = true;
      button.textContent = "Fetching…";
      void Promise.all(pending.map((ref) => openBlob(ref).catch(() => null)))
        .then(() => {
          button.remove();
          // Asked for by hand, so the redraw asks for nothing more: whatever
          // failed here is offered again rather than retried unprompted.
          return fillBody(item, slot, card, false);
        })
        .catch((err: unknown) => {
          button.disabled = false;
          button.textContent = `Failed: ${message(err)}`;
        });
    });
    return button;
  }

  /** Remote image object URLs for the open message, by their absolute address. */
  let remoteHeld = new Map<string, string>();
  /** The message the held remote URLs belong to. */
  let remoteMessage: string | null = null;

  /**
   * A remote load belongs to one message. The URLs are released when another
   * message opens, not on every repaint, or a redraw would revoke the very
   * images it was about to draw.
   */
  function releaseRemoteImages(): void {
    for (const url of remoteHeld.values()) URL.revokeObjectURL(url);
    remoteHeld = new Map();
    remoteMessage = null;
  }

  /**
   * Fetch remote image bytes through the page. No signer round-trips, so no
   * budget: a permitted sender's images all come, and a failure is quiet — the
   * placeholder already says the image is blocked.
   */
  async function loadRemoteImages(addresses: readonly string[]): Promise<void> {
    await Promise.all(addresses.map(async (address) => {
      const fetched = await fetchRemoteImage(address);
      if (!fetched) return;
      remoteHeld.set(address, URL.createObjectURL(
        new Blob([fetched.bytes.slice().buffer], { type: fetched.type }),
      ));
    }));
  }

  /**
   * What a body's remote images need this render.
   *
   * A permitted sender's images load themselves, and the body says so — the
   * reader must never wonder why a tracker was allowed. Any other sender's stay
   * blocked, with the two controls that open them.
   */
  function paintRemoteImages(
    item: StoredMail,
    slot: HTMLElement,
    card: HTMLElement,
    html: string,
  ): void {
    const remote = html ? remoteImages(html) : [];
    if (remote.length === 0) return;
    if (remoteMessage !== item.id) {
      releaseRemoteImages();
      remoteMessage = item.id;
    }
    const permitted = !item.sent && imageSenders.includes(item.senderAddress.toLowerCase());
    const missing = remote.filter((address) => !remoteHeld.has(address));
    if (permitted) {
      if (missing.length > 0) {
        void loadRemoteImages(missing).then(() => {
          if (slot.isConnected) void fillBody(item, slot, card, false);
        });
      }
      slot.append(el("div", { class: "remote-note" }, "Remote images load for this sender."));
    } else if (missing.length > 0) {
      card.append(remoteControls(item, missing, slot, card));
    } else {
      slot.append(el("div", { class: "remote-note" }, "Remote images loaded for this message."));
    }
  }

  /**
   * The two ways a reader may open a sender's remote images: for this message,
   * or for the sender from now on. They stay separate controls, because the
   * second is a per-sender tracking opt-in and must not be clickable by
   * accident.
   */
  function remoteControls(
    item: StoredMail,
    addresses: readonly string[],
    slot: HTMLElement,
    card: HTMLElement,
  ): HTMLElement {
    const fetch = (): readonly string[] => addresses.filter((a) => !remoteHeld.has(a));
    const load = el("button", { class: "secondary load-images", type: "button" }, "Load remote images");
    load.addEventListener("click", () => {
      load.disabled = true;
      load.textContent = "Fetching…";
      void loadRemoteImages(fetch()).then(() => {
        controls.remove();
        void fillBody(item, slot, card, false);
      });
    });
    const always = el(
      "button",
      { class: "secondary load-images", type: "button",
        title: `Always load remote images from ${item.senderAddress}` },
      "Always load from this sender",
    );
    always.addEventListener("click", () => {
      always.disabled = true;
      permitImageSender(item.senderAddress);
      status.textContent = "Images from this sender load from now on.";
      void loadRemoteImages(fetch()).then(() => {
        controls.remove();
        void fillBody(item, slot, card, false);
      });
    });
    const controls = el(
      "div",
      { class: "remote-controls" },
      load,
      item.sent ? "" : always,
    );
    return controls;
  }

  /** The URLs one run of `fillBody` made, revoked when that run is replaced. */
  let bodyUrls: string[] = [];

  /**
   * Release the body URLs of the previous run.
   *
   * A body redraws on the same slot when the reader asks for the deferred
   * images, and `fillBody` clears the slot first. A URL scoped to the shell
   * therefore outlived the `<img>` that used it and stayed alive until the
   * reader left the message. The run owns its URLs instead.
   */
  function releaseBodyImages(): void {
    for (const url of bodyUrls) URL.revokeObjectURL(url);
    bodyUrls = [];
  }

  function objectUrl(bytes: Uint8Array, contentType: string, into: string[] = imageUrls): string {
    const url = URL.createObjectURL(new Blob([bytes.slice().buffer], {
      type: contentType || "application/octet-stream",
    }));
    into.push(url);
    imageUrls.push(url);
    return url;
  }

  function releaseImages(): void {
    releaseBodyImages();
    for (const url of imageUrls) URL.revokeObjectURL(url);
    imageUrls = [];
  }

  /**
   * One file, one pill.
   *
   * Nothing is fetched until it is clicked: an attachment is a file the reader
   * may want, not one they asked for, and every fetch is a signature.
   */
  function attachment(file: BlobRef): HTMLElement {
    const name = file.filename || "(unnamed)";
    const label = el("span", { class: "name" }, name);
    const pill = el(
      "button",
      { class: "attachment", type: "button", title: `Download ${name}` },
      icon("download", 16),
      label,
      el("span", { class: "size" }, kb(file.size)),
    );
    pill.addEventListener("click", () => {
      pill.disabled = true;
      label.textContent = "Fetching…";
      void openBlob(file)
        .then((bytes) => {
          download(name, file.content_type, bytes);
          label.textContent = name;
        })
        .catch((err: unknown) => {
          label.textContent = `${name} — ${message(err)}`;
        })
        .finally(() => {
          pill.disabled = false;
        });
    });
    return pill;
  }

  /** Cache first: with a phone signer every fetch is another approval. */
  async function openBlob(ref: BlobRef): Promise<Uint8Array> {
    const cached = await cachedBlob(ref.sha256);
    if (cached) return cached;
    const bytes = await fetchBlob(ref, session.signer);
    await cacheBlob(ref.sha256, bytes);
    return bytes;
  }

  function action(
    name: IconName,
    label: string,
    on: boolean,
    run: () => Promise<void>,
  ): HTMLButtonElement {
    const button = el("button", {
      class: `act${on ? " is-on" : ""}`, type: "button",
      "aria-pressed": String(on), "aria-label": label, title: label,
    }, icon(name, 18));
    button.addEventListener("click", () => void run());
    return button;
  }

  /** First contact: exactly two targets, no third option, and it is permanent. */
  async function paintScreener(): Promise<void> {
    clear(screenerSlot);
    const waiting = (await byDisposition("screener")).filter(
      (m) => !decided[m.senderAddress] && !session.allowlist.has(m.senderAddress),
    );
    const first = waiting[0];
    if (!first) return;

    const accept = el("button", { class: "accept", type: "button" }, "Let In");
    const reject = el("button", { class: "reject", type: "button" }, "Screen Out");

    accept.addEventListener("click", () => {
      void decide(first, true);
    });
    reject.addEventListener("click", () => {
      void decide(first, false);
    });

    screenerSlot.append(
      el("div", { class: "label" }, el("span", { class: "dot" }), el("span", {}, `${BOX_LABEL["screener"]} — First Contact`)),
      el("div", { class: "who" }, nameOf(first)),
      screenerPreview(first, openBlob),
      el("div", { class: "note" }, "Decide once. It is remembered."),
      el("div", { class: "actions" }, accept, reject),
    );
  }

  /**
   * Both answers are final and both take effect here, not at the next sync:
   * the mail already held is refiled or dropped before the card is redrawn.
   */
  async function decide(item: StoredMail, letIn: boolean, from: Disposition = "screener"): Promise<void> {
    const address = item.senderAddress;
    decided = { ...decided, [address]: letIn ? "in" : "out" };
    await recordScreening(address, letIn ? "in" : "out");

    if (!letIn) {
      // Screening out stays local: the relay is never told who was refused.
      await forgetSender(address);
      status.textContent = `${address} is screened out.`;
      await paint();
      return;
    }

    session.allowlist.add(address);
    await refileSender(address, from, "inbox");
    await paint();
    void publishLetIns();
  }

  /**
   * Put every sender let in here onto the published contact list.
   *
   * The list is one replaceable event, so two Let In clicks publishing their
   * own versions would leave whichever the relay kept. Publishes queue, each
   * writes the union of what the relay holds and every Let In this device has
   * recorded, and each is stamped after the last. A failure is retried by the
   * next sync, because the Let Ins it carried are still on disk.
   */
  let contactsSeenAt = 0;
  let contactsChain: Promise<void> = Promise.resolve();
  function publishLetIns(): Promise<void> {
    contactsChain = contactsChain.then(async () => {
      try {
        const want = Object.entries(await screenings())
          .filter(([, decision]) => decision === "in")
          .map(([address]) => address);
        // Read fresh inside the queue: a list read before an earlier publish
        // in it landed would drop what that publish added.
        const current = await fetchContacts(
          session.relay, session.pubkey, session.signer, session.ingestPubkey,
        );
        const have = new Set(current?.addresses ?? []);
        const missing = want.filter((address) => !have.has(address));
        if (missing.length === 0) return;
        status.textContent = "Publishing the updated contact list…";
        const event = await publishContacts(
          session.relay, session.signer, session.ingestPubkey,
          [...have, ...missing], (current?.version ?? 0) + 1, contactsSeenAt,
        );
        contactsSeenAt = event.created_at;
        status.textContent = `${missing.join(", ")} will reach the ${BOX_LABEL["inbox"]} from now on.`;
      } catch (err) {
        status.textContent = `Could not update the contact list: ${message(err)}`;
      }
    });
    return contactsChain;
  }

  await paint();

  // A tab closed with a half-written message reopens on it. Only the draft that
  // was on screen comes back: one merely held must not cover the mail.
  const unfinished = await composing();
  if (unfinished) {
    const draft = await loadDraft(unfinished);
    if (draft) await openDraft(draft);
    else await setComposing(null);
  }

  // Both fill in behind the local index rather than in front of it. The sync
  // goes through the poll, which is the one gate: the first run, the minute
  // tick and coming back to the tab all arrive here and none of them overlaps.
  void loadAccount();

  /** Said once. A line that flashes every minute reads as a fault, not as life. */
  let synced = false;
  const poll = pollWhileVisible(sync);
  release.push(() => poll.stop());
  poll.now();

  /**
   * New mail as it lands, rather than on the next beat.
   *
   * The subscription only says that something arrived; the sync is still what
   * opens it, through the same gate, so a burst of wraps is one sync and a
   * phone signer is asked once per burst, not once per wrap. A hidden tab
   * hears it and does nothing until it is looked at.
   */
  let unwatch: (() => void) | null = null;
  let arrived: ReturnType<typeof setTimeout> | null = null;
  function watchForMail(): void {
    if (unwatch) return;
    unwatch = () => {};
    session.relay
      .watch(
        { kinds: [KIND_GIFT_WRAP], "#p": [session.pubkey], limit: 0 },
        () => {
          if (arrived) clearTimeout(arrived);
          arrived = setTimeout(() => poll.now(), LIVE_SETTLE_MS);
        },
        () => {
          unwatch = null;
        },
      )
      .then((stop) => {
        unwatch = stop;
      })
      .catch(() => {
        unwatch = null;
      });
  }
  release.push(() => {
    unwatch?.();
    if (arrived) clearTimeout(arrived);
  });
  /** A sender looked up once per session: two relay queries and a fetch each. */
  const identities = new Map<string, Promise<Identity>>();
  const lookups = defaultLookups((pubkey) => fetchProfile(pubkey));

  /** An email or a DM, or null for a rumor this client does not read. */
  async function openWrap(wrap: Parameters<typeof unwrap>[0]): Promise<Mail | null> {
    const { seal, rumor } = await unwrap(wrap, session.signer);
    if (rumor.kind === KIND_MAIL) return mailOf(wrap.id, rumor);
    if (rumor.kind !== KIND_CHAT) return null;
    let who = identities.get(rumor.pubkey);
    if (!who) {
      who = identify(rumor.pubkey, lookups);
      identities.set(rumor.pubkey, who);
    }
    return dmOf(wrap.id, seal, rumor, await who, session.pubkey);
  }

  async function sync(): Promise<void> {
    try {
      if (!synced) status.textContent = "Syncing…";
      // Read before the mail, so a message deleted elsewhere is never opened
      // here; then publish anything still queued here, which is the retry.
      await deleter.readRemote();
      await deleter.flush();
      // A decision this device has not published goes first, so the relay's
      // older map cannot overwrite it on the way in.
      if (await workflowPending()) await (wfChain = wfChain.then(publishWorkflowMap));
      await readWorkflow();
      await readImageSenders();
      const since = await lastSync();
      const wraps = await session.relay.list(mailFilter(session.pubkey, since));
      const known = await knownIds();
      let added = 0;
      let newest = since ?? 0;
      for (const wrap of wraps) {
        newest = Math.max(newest, wrap.created_at);
        // Deleted. The relay still holds the wrap and always will, so this is
        // the check that keeps a deleted message deleted.
        if (deleter.has(wrap.id)) continue;
        // Already held. Not new, not counted, and not decrypted again.
        if (known.has(wrap.id)) continue;
        try {
          const mail = await openWrap(wrap);
          if (!mail) continue;
          const stored = toStored(mail);
          // A decision already made is applied on the way in, so a screened-out
          // sender never reappears and a let-in one never waits for postrd.
          const verdict = decided[stored.senderAddress];
          if (verdict === "out") continue;
          if (verdict === "in" && (stored.disposition === "screener" || stored.disposition === "quarantine")) {
            stored.disposition = "inbox";
            stored.verdict = { ...stored.verdict, disposition: "inbox" };
          }
          await put(stored);
          added++;
        } catch (err) {
          // A wrap we cannot open is someone else's, not a fault.
          if (!isNotOurs(err)) console.warn("could not open a wrap", err);
        }
      }
      if (newest) await setLastSync(newest);
      if (added > 0 && wfFlags && !(await workflowPending())) await applyWorkflow(wfFlags);

      const contacts = await fetchContacts(
        session.relay, session.pubkey, session.signer, session.ingestPubkey,
      );
      if (contacts) session.allowlist = new Allowlist(contacts.addresses);
      // A Let In whose publish failed is still on disk; finish it.
      const unpublished = Object.entries(await screenings())
        .some(([address, decision]) => decision === "in" && !session.allowlist.has(address));
      if (unpublished) void publishLetIns();
      // A sender let in on another device — from the Screener or with Not
      // spam — leaves mail waiting in those boxes here; the list is the
      // authority, so the local index follows it.
      for (const from of ["screener", "quarantine"] as const) {
        for (const address of new Set(
          (await byDisposition(from))
            .map((m) => m.senderAddress)
            .filter((a) => session.allowlist.has(a)),
        )) {
          await refileSender(address, from, "inbox");
        }
      }

      // Only now, with the contact list read and applied: Spam expiring before
      // that would delete, everywhere, mail another device rescued.
      for (const id of await expiredSpam(Math.floor(Date.now() / 1000) - SPAM_DAYS * 86_400)) {
        await deleter.delete(id);
      }

      // The stamp is the whole proof the client is still watching: it moves
      // every minute, in place, in the fixed-width line, so nothing shifts and
      // nothing spins. A line that stopped moving is a client that stopped.
      status.textContent =
        `${added} new · ${session.allowlist.size} known senders · checked ${clock(Date.now())}`;
      synced = true;
      // A dropped socket closes the subscription; the next good sync reopens it.
      watchForMail();
      await paint();
    } catch (err) {
      status.textContent = `Offline: ${message(err)}. Showing local mail.`;
      // Reported, and passed on: the poll lengthens its beat only on a failure.
      throw err;
    }
  }
}

/** Hand the bytes to the browser as a file. Nothing is uploaded anywhere. */
function download(filename: string, contentType: string, bytes: Uint8Array): void {
  const url = URL.createObjectURL(new Blob([bytes.slice().buffer], {
    type: contentType || "application/octet-stream",
  }));
  const link = el("a", { href: url, download: filename });
  link.click();
  // Revoked on the next turn: revoking synchronously races the download.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function kb(size: number): string {
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

/** A DM has no subject as a rule; its opening words stand in for one. */
function headline(item: StoredMail): string {
  if (item.subject) return item.subject;
  if (item.origin === "nostr") return item.text?.trim().split("\n")[0] || "(empty message)";
  return "(no subject)";
}

/** The one mark that says a message came over Nostr rather than email. */
function originTag(item: StoredMail): HTMLElement | string {
  return item.origin === "nostr" ? el("span", { class: "tag tag-nostr" }, "#nostr") : "";
}

/** Kev's category when it has one, the box it landed in otherwise. */
function tagFor(item: StoredMail): string {
  const category = item.verdict?.category?.trim();
  if (category) return category.replace(/\s+/g, "");
  return (BOX_LABEL[item.disposition] ?? item.disposition).replace(/\s+/g, "");
}

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
